import { Card } from "@/components/ui/card"
import type { CockpitDay, CockpitPost } from "@shared/dashboard"
import { DAY_LABELS, dayNumber } from "@/lib/cockpitFormat"
import { WeekPostCard } from "./WeekPostCard"

// The week as a strip: Mo–So columns with post cards; a trailing "Zur Freigabe" column
// appears only while posts wait for the customer (they have no date yet — the DFY
// drainer assigns slots after approval).

interface WeekStripProps {
  days: CockpitDay[]
  waiting: CockpitPost[]
  onEmptyDayClick?: () => void
}

function Legend() {
  const items: { cls: string; label: string }[] = [
    { cls: "bg-primary", label: "Veröffentlicht" },
    { cls: "bg-zinc-400 dark:bg-zinc-500", label: "Geplant" },
    { cls: "bg-orange-500", label: "Wartet auf Sie" },
  ]
  return (
    <div className="ml-auto hidden gap-3.5 text-[11.5px] text-muted-foreground sm:flex" aria-hidden="true">
      {items.map((i) => (
        <span key={i.label} className="flex items-center gap-1.5">
          <span className={`inline-block h-2 w-2 rounded-full ${i.cls}`} />
          {i.label}
        </span>
      ))}
    </div>
  )
}

export function WeekStrip({ days, waiting, onEmptyDayClick }: WeekStripProps) {
  const columns = waiting.length > 0 ? 8 : 7
  const first = days[0]
  const last = days[days.length - 1]
  const range = first && last ? `${DAY_LABELS[first.weekday]} ${dayNumber(first.date)} bis ${DAY_LABELS[last.weekday]} ${dayNumber(last.date)}` : ""

  return (
    <Card className="overflow-hidden" data-testid="cockpit-week">
      <div className="flex items-center gap-3.5 border-b border-border/60 px-4 py-3 sm:px-5">
        <h2 className="font-playfair text-[17px] font-bold">Ihre Woche</h2>
        <span className="text-xs text-muted-foreground">{range}</span>
        <Legend />
      </div>
      <div
        className={`grid grid-cols-2 sm:grid-cols-4 ${columns === 8 ? "lg:grid-cols-8" : "lg:grid-cols-7"}`}
        data-testid="week-days"
      >
        {days.map((day) => (
          <div
            key={day.date}
            className={`flex min-h-[170px] flex-col gap-2 border-b border-r border-border/50 px-2 pb-3 pt-2.5 last:border-r-0 lg:border-b-0 ${day.isToday ? "bg-primary/5" : ""}`}
            data-testid={`week-day-${day.weekday}`}
          >
            <div className="flex items-baseline justify-between">
              <span className={`text-[11px] font-bold uppercase tracking-[0.06em] ${day.isToday ? "text-primary" : "text-muted-foreground"}`}>
                {DAY_LABELS[day.weekday]}
              </span>
              <span className="text-xs tabular-nums text-muted-foreground">{dayNumber(day.date)}</span>
            </div>
            {day.posts.map((p) => (
              <WeekPostCard key={p.id} post={p} />
            ))}
            {day.posts.length === 0 && (
              onEmptyDayClick && day.weekday < 5 ? (
                <button
                  type="button"
                  onClick={onEmptyDayClick}
                  className="mt-auto rounded-md border border-dashed border-border py-3 text-[11px] text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                  aria-label={`${DAY_LABELS[day.weekday]} ist noch frei, Foto hochladen`}
                >
                  Frei
                </button>
              ) : (
                <p className="mt-auto py-3 text-center text-[11px] text-muted-foreground">Pause</p>
              )
            )}
          </div>
        ))}
        {waiting.length > 0 && (
          <div
            className="flex min-h-[170px] flex-col gap-2 border-l border-orange-200 bg-orange-50/60 px-2 pb-3 pt-2.5 dark:border-orange-900/50 dark:bg-orange-950/20"
            data-testid="week-waiting"
          >
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-orange-600 dark:text-orange-400">Zur Freigabe</span>
              <span className="text-xs tabular-nums text-muted-foreground">{waiting.length}</span>
            </div>
            {waiting.map((p) => (
              <WeekPostCard key={p.id} post={p} />
            ))}
          </div>
        )}
      </div>
    </Card>
  )
}
